import React from "react";
import {
  DetailsList,
  DetailsListLayoutMode,
  Selection,
  SelectionMode,
  IColumn,
} from "@fluentui/react/lib/DetailsList";
import { MarqueeSelection } from '@fluentui/react/lib/MarqueeSelection';
import { SkeletonCell, TicketProp } from './TicketListBoard';
import { ConvertDateToLocale } from '../../common/ConvertDate';
import { AppStateValue, useSetState } from '../../../AppState';
import { UserAvatar } from '../UserAvatar';
import { isCloseFilter } from '../../../tikit/ticketHelper';
import { Link, RouteComponentProps, useLocation, withRouter } from 'react-router-dom';
import { SpeakerPersonIcon, ProviderConsumer as FluentUIThemeConsumer, AcceptIcon, Tooltip } from '@fluentui/react-northstar';
import _ from 'lodash';
import { ThemeColorScheme } from '../../common/TeamsTheme';
import { PRODUCT_LICENSE, ListColumnKey, MyWorkListViewType, markdownComponents } from '../../utils/constants';
import { getCachedFeature } from '../../cache/FeatureCache';
import { EnumSortOrderType } from '../../../tikit/toolbar/CustomViewContextProvider';
import { GroupUsersIcon } from '../../../tikit/Request/EditRequestDetails/GroupUsersCard/GroupUsersIcon';
import { TicketLifecyclePhaseElement } from '../TicketLifecycleTab/TicketLifecyclePhaseRibbon';
import { SlaPills } from '../SlaPills/SlaPills';
import { Route } from '../../../../route-constants';
import {  i18n, openGroupChat } from '../../utils/helper';
import { DateTime } from 'luxon';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { truncateString } from '../../../tikit/MarkdownHelper';
import { AsyncCircleImage } from '../CircleImages';
import { ICustomProperty } from "../../interfaces/customproperties.interface";
import { getCustomPropertyDisplayData } from "../TicketDetailInfo/TicketDetailInfo";
import { DurationText } from "../TicketTimeTracking/DurationText";

const { t } = i18n();

export interface State {
  columns: IColumn[];
  items: Ticket[];
  selectedItems: Ticket[];
  analystAccess: boolean;
}

type PropsType = RouteComponentProps<{}> & {
  tickets: Ticket[];
  onTicketsSelected: (tickets: Ticket[]) => void;
  currentState: AppStateValue;
  useTicketType: boolean;
  useTeam: boolean;
  useTicketLifecycle: boolean;
  useSLA: boolean;
  useResolutionNote: boolean;
  useGroupChat: boolean;
  useCustomProperties: boolean;
  useTimeTracking: boolean;
  setListStates: React.Dispatch<React.SetStateAction<ListStatesType>>;
  selectedOrderedColumns: null | (number | string)[];
  sortOrderApplied: {
    orderBy: ISortOrderBy;
    orderType: ISortOrderType;
  };
  clickColumnCallback: (x: IColumn) => void;
  customViewStates: CustomViewStatesType;
};

class ClassDetailsList extends React.Component<PropsType, State> {
  private _selection: Selection;

  constructor(props: PropsType) {
    super(props);

    this._selection = new Selection({
      onSelectionChanged: () => {
        this.setState({
          selectedItems: this._getSelectionDetails(),
        });
      },
    });

    this.props.setListStates((prev) => ({
      ...prev,
      rowsSelection: this._selection,
    }));

    
    let cols = this._columns.slice();
    cols = this.filterColumnsByFeature(cols, this.props);

    if(!this.isViewEdited() && (this.props.selectedOrderedColumns?.length ?? 0) == 0)
      cols = cols.filter(c =>  Number(c.key) < 1000);
    this.state = {
      items: this.props.tickets,
      columns: cols,
      selectedItems: [],
      analystAccess: false,
    };
  }

  isViewEdited = () => this.props.customViewStates.selectedCustomView != undefined;

  getLayoutMode = () => {
    return (localStorage.getItem("TikitViewsHorizontalScroll") ?? "true") === "true"
      ? DetailsListLayoutMode.fixedColumns
      : DetailsListLayoutMode.justified;
  };

  public render() {
    const { columns, items } = this.state;

    return (
      <FluentUIThemeConsumer
        render={(globalTheme) => (
          <div
            className={`py-3.5 px-2.5 min-h-32 main-ticket-list`}
            style={{
              backgroundColor: "var(--mgt-theme-background2)",
              ...ThemeColorScheme(globalTheme.siteVariables),
            }}
          >
            <div>
              <MarqueeSelection selection={this._selection}>
                <DetailsList
                  items={items}
                  compact={false}
                  columns={columns}
                  selectionMode={
                    isCloseFilter(this.props.currentState.listFilter) ||
                    !this.state.analystAccess
                      ? SelectionMode.none
                      : SelectionMode.multiple
                  }
                  getKey={this._getKey}
                  setKey="multiple"
                  layoutMode={this.getLayoutMode()}
                  isHeaderVisible={true}
                  selection={this._selection}
                  selectionPreservedOnEmptyClick={true}
                  enterModalSelectionOnTouch={true}
                  ariaLabelForSelectionColumn="Toggle selection"
                  ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                  checkButtonAriaLabel="select row"
                  className="details-list-table"
                />
              </MarqueeSelection>
            </div>
          </div>
        )}
      />
    );
  }

  public componentDidMount() {
    (async () => {
      let isAnalystAccess = await getCachedFeature(
        PRODUCT_LICENSE.AnalystAccess
      );
      this.setState({
        items: this.props.tickets,
        analystAccess: isAnalystAccess,
      });
    })();
  }

  public filterColumnsByFeature(cols: IColumn[], props: PropsType) {
    let excludeCols = [];
    if (!props.useTicketType) excludeCols.push("TicketType");
    if (!props.useTeam) excludeCols.push("TeamName");
    if (!props.useTicketLifecycle) {
      excludeCols.push("TicketLifecycleName");
      excludeCols.push("TicketLifecyclePhaseName");
    }
    if (!props.useSLA) excludeCols.push("SLA");

    if (!props.useResolutionNote){
      excludeCols.push("InternalNote");
      excludeCols.push("ResolutionCategory");
      excludeCols.push("Note");
      excludeCols.push("DraftKB");
    }

    if (!props.useGroupChat) {
      excludeCols.push("GroupChat");
    }

    // The column picker gates this too (CustomViewPopup/Content.tsx), but that only stops
    // it being *chosen*. Without this the grid still draws it - as the whole built-in set
    // when no view is selected, and inside any view saved before the feature was turned
    // off - giving an unlicensed tenant a Total Agent Time column reading 0m on every row.
    if (!props.useTimeTracking) {
      excludeCols.push("TotalTimeSpentMinutes");
    }
    if(excludeCols.length > 0)
      cols = cols.filter(x => !excludeCols.includes(x.fieldName));

    if (!props.useCustomProperties) 
      cols = cols.filter(c =>  Number(c.key) < 1000);

    return cols;
  }

  public getOrderedColumns(cols: IColumn[]) {
    const selectedOrderedColumns = [...this.props.selectedOrderedColumns];
    if (selectedOrderedColumns?.length > 0) {
      const filteredCols = cols.filter((col) =>
        selectedOrderedColumns.includes(Number(col.key))
      );

      const orderedColumns = selectedOrderedColumns.reduce((acc, value) => {
        const matchedColumn = filteredCols.find(
          (column) => Number(column.key) === value
        );
        return [...acc, matchedColumn];
      }, [] as IColumn[]);
      return orderedColumns.filter((item) => item);
    }

    if(!this.isViewEdited() && (this.props.selectedOrderedColumns?.length ?? 0) == 0)
      cols = cols.filter(c =>  Number(c.key) < 1000);

    return cols;
  }

  public componentDidUpdate(previousProps: PropsType, previousState: State) {
    if (
      previousProps.tickets.length !== this.props.tickets.length ||
      _.differenceBy(previousProps.tickets, this.props.tickets, "Title")
        .length > 0
    ) {
      this.setState({ items: [...this.props.tickets] });
    }

    if (
      previousProps.tickets.every((x) => !x.TicketSlas) &&
      this.props.tickets.some((x) => x.TicketSlas?.length > 0)
    ) {
      this.setState({ items: [...this.props.tickets] });
    }

    if (
      JSON.stringify(previousProps.selectedOrderedColumns) !==
      JSON.stringify(this.props.selectedOrderedColumns)
    ) {
      let cols = this._columns.slice();
      cols = this.filterColumnsByFeature(cols, this.props);

      const columns = this.getOrderedColumns(cols);
      this.setState({ columns });
    }
  }

  private _getKey(item: any, index?: number): string {
    return item.key;
  }

  private _getSelectionDetails(): Ticket[] {
    this.props.onTicketsSelected(this._selection.getSelection() as Ticket[]);
    return this._selection.getSelection() as Ticket[];
  }

  private _onColumnClick = (
    ev: React.MouseEvent<HTMLElement>,
    column: IColumn
  ): void => {
    this.props.clickColumnCallback(column);
  };

  private _requestLength =
    (localStorage.getItem("TikitRequestLength") || "abbreviated") == "full";

  private _cols: _ColsType = {
    Id: [ListColumnKey.Id, t('ticket.ticket-list.id'), 'Id', 30, 30, null, true, renderRecordValue('Id', this)],
    Type: [ListColumnKey.Type, t('ticket.ticket-list.type'), 'TicketType', 100, 100, null, null, renderRecordValue('Type', this)],
    Request: [ListColumnKey.Title, t('ticket.ticket-list.request'), 'Title', (this._requestLength ? 100 : 200), (this._requestLength ? 200 : null), (this._requestLength ? true : null), true, renderRecordValue('Request', this)],
    Status: [ListColumnKey.Status, t('ticket.ticket-list.status'), 'Status', 125, 125, null, null, renderRecordValue('Status', this)],
    GroupChat: [ListColumnKey.GroupChat, t('ticket.ticket-list.group-chat'), 'GroupChat', 100, 100, null, null, renderRecordValue('GroupChat', this)],
    Requester: [ListColumnKey.Requester, t('ticket.ticket-list.requester'), 'RequesterName', 150, 150, null, null, renderRecordValue('Requester', this)],
    Category: [ListColumnKey.Category, t('ticket.ticket-list.category'), 'Category', 125, 125, null, null, renderRecordValue('Category', this)],
    Priority: [ListColumnKey.Priority, t('ticket.ticket-list.priority'), 'Priority', 100, 100, null, null, renderRecordValue('Priority', this)],
    SLAs: [ListColumnKey.SLA, t('ticket.ticket-list.sla'), 'SLA', 100, 100, null, null, renderRecordValue('SLAs', this)],
    Team: [ListColumnKey.Team, t('ticket.ticket-list.team'), 'TeamName', 100, 100, null, null, renderRecordValue('Team', this)],
    Group: [ListColumnKey.Group, t('ticket.ticket-list.group'), 'GroupName', 100, 100, null, null, renderRecordValue('Group', this)],
    Assignee: [ListColumnKey.Assignee, t('ticket.ticket-list.assignee'), 'AssigneeName', 100, 100, null, true, renderRecordValue('Assignee', this)],
    Lifecycle: [ListColumnKey.Lifecycle, t('ticket.ticket-list.lifecycle'), 'TicketLifecycleName', 100, 100, null, null, renderRecordValue('Lifecycle', this)],
    Phase: [ListColumnKey.Phase, t('ticket.ticket-list.phase'), 'TicketLifecyclePhaseName', 100, 100, null, null, renderRecordValue('Phase', this)],
    'Last Modified Date': [ListColumnKey.LastModifiedDate, t('ticket.ticket-list.last-modified-date'), 'ModifiedDate', 100, 100, null, null, renderRecordValue('Last Modified Date', this)],
    'Created Date': [ListColumnKey.CreatedDate, t('ticket.ticket-list.created-date'), 'CreatedDate', 100, 100, null, null, renderRecordValue('Created Date', this)],
    'Due Date': [ListColumnKey.DueDate, t('ticket.ticket-list.due-date'), 'DueDate', 100, 100, null, null, renderRecordValue('Due Date', this)],
    'Resolution Date': [ListColumnKey.ResolutionDate, t('ticket.ticket-list.resolution-date'), 'ResolutionDate', 100, 100, null, null, renderRecordValue('Resolution Date', this)],
    ResolutionCategory : [ListColumnKey.ResolutionCategory, t('resolution-note.resolution-category'), 'ResolutionCategory', 100, 100, null, null, renderRecordValue('ResolutionCategory', this)],
    Note: [ListColumnKey.Note, t('resolution-note.resolution-note'), 'Note', (this._requestLength ? 100 : 200), (this._requestLength ? 100 : 200), (this._requestLength ? true : null), true, renderRecordValue('Note', this)],
    InternalNote: [ListColumnKey.InternalNote, t('resolution-note.resolution-note-internal'), 'InternalNote', (this._requestLength ? 100 : 200), (this._requestLength ? 100 : 200), (this._requestLength ? true : null), true, renderRecordValue('InternalNote', this)],
    DraftKB: [ListColumnKey.DraftKB, t('resolution-note.draft-kb'), 'DraftKB', 100, 100, null, null, renderRecordValue('DraftKB', this)],
    // Wider than the default 100 only so the header is not truncated - "Total Agent
    // Time" is a longer label than most, and several translations are longer still.
    TotalAgentTime: [ListColumnKey.TotalAgentTime, t('ticket.ticket-list.total-agent-time'), 'TotalTimeSpentMinutes', 120, 138, null, null, renderRecordValue('TotalAgentTime', this)]
  };

  private _colsWithProps = () => {
    if(this.props.useCustomProperties){
      let customPropertyColId = 1000;
      this.props.currentState?.customProperties.filter(p => !p.IsHidden).sort((a, b) => a.Order - b.Order).forEach(cp => {
        this._cols[cp.PropertyId] = [customPropertyColId + cp.Id, cp.Name, cp.PropertyId, 100, 100, null, null, renderCPRecordValue(cp)];
      });
    }
    return this._cols;
  }

  private _columns: IColumn[] = Object.values(this._colsWithProps()).map((value) => {
    const orderBy = this.props.sortOrderApplied.orderBy;
    const orderType = this.props.sortOrderApplied.orderType;

    return {
      key: value[0] + "",
      name: value[1],
      fieldName: value[2],
      minWidth: value[3],
      maxWidth: value[4],
      isMultiline: value[5],
      isPadded: value[6],
      onRender: value[7],
      isSorted: value[0] === orderBy ? true : null,
      isSortedDescending: value[0] === orderBy && orderType === EnumSortOrderType.desc,
      onColumnClick: this._onColumnClick,
      isResizable: true,
      isCollapsible: true,
    };
  });
}

export default withRouter(ClassDetailsList);

type ColValuesType = [
  ISortOrderBy,
  string,
  string,
  number,
  number,
  boolean | null,
  boolean | null,
  (item: TicketProp) => JSX.Element
];
type _ColsType = {
  [x in TicketColumnNameType]: ColValuesType;
};

interface ILinkRecord {
  ticketId: number;
  itemId?: number;
  value: number | string;
  title: string;
  type?: number;
}
export const LinkRecord = ({
  ticketId: ticId,
  itemId,
  value,
  title,
  type,
}: ILinkRecord) => {
  const location = useLocation();
  const ticketId = ticId.toString();
  const setAppState = useSetState();

  const isTaskOrApproval = [
    MyWorkListViewType.Approvals,
    MyWorkListViewType.Tasks,
  ].includes(type);
  const onClickLink = () => {
    if (isTaskOrApproval) {
      setAppState((prev) => ({
        ...prev,
        isRouteFromMyWork: true,
        selectedTaskOrApproval: { type, id: itemId },
      }));
    }
  };

  const isMyGroupWorkListView = location.pathname === Route.myGroupWork.list;
  const isMyWorkListView = location.pathname === Route.myWork.list;
  let path = "";
  let from = "";

  if (isMyGroupWorkListView || isMyWorkListView) {
    const route = isMyGroupWorkListView
      ? Route.myGroupWork.editTicket
      : Route.myWork.editTicket;
    path = `${route}/${ticketId}`;
    from = isMyGroupWorkListView ? "mygroupwork" : "mywork";
  } else {
    path = `/tickets/${ticketId}`;
    from = "";
  }

  return (
    <FluentUIThemeConsumer
      render={(globalTheme) => (
        <Tooltip
          align={"center"}
          position={"below"}
          trigger={<span
                className='class-details-grid-span'
                style={{
                  cursor: 'pointer',
                  color: globalTheme.siteVariables.colorScheme.brand.foregroundActive,
                }}
              >
            <Link to={{pathname: path, state: { from }}} onClick={onClickLink}>{value}</Link>
          </span>}
          content={title}
        />
      )}
    />
  );
};

export const SLAPills = (ticketSlas) => {
  return (
    <FluentUIThemeConsumer
      render={(globalTheme) => (
        <SlaPills
          ticketSlas={ticketSlas.ticketSlas}
          svgGap={false}
          viewType="List"
        />
      )}
    />
  );
};

const _getRequester = (item: TicketProp, thisContext: any) => {
  const user = item.Requester;
  const affectedUsers = item.AffectedUsers;

  if (affectedUsers.length > 0) {
    const users = user ? [{ PlatformUserId: user.Id, PlatformUser: user }, ...affectedUsers] : affectedUsers;
    return _getUsers(users);
  }

  return _getUser(user?.AadObjectId ? user?.AadObjectId : user?.UserName, user?.FullName);
};

const _getAssignee = (item: TicketProp, thisContext: any) => {
  const user = item.Assignee;
  const collaborators = item.TicketCollaborators;

  if (collaborators.length > 0) {
    const users = user ? [{ PlatformUserId: user.Id, PlatformUser: user }, ...collaborators] : collaborators;
    return _getUsers(users);
  }

  return _getUser(user?.AadObjectId ? user?.AadObjectId : user?.UserName, user?.FullName);
};


export const MarkdownTooltip = (prop) =>{
  let detail = prop?.details ?? "";
  let cssClass = prop.context._requestLength ? "class-details-grid-span" : "class-details-grid-span-abbreviated";
  return (
    <FluentUIThemeConsumer
      render={(globalTheme) => (
        <Tooltip
        align={"center"}
        position={"below"}
        trigger={<span className={cssClass}>
          <Markdown remarkPlugins={[remarkGfm, remarkBreaks]} components={markdownComponents} >
            {truncateString(detail, 350)}
          </Markdown>
          </span>}
        content={<span>
          <Markdown remarkPlugins={[remarkGfm, remarkBreaks]} components={markdownComponents} >
            {detail}
          </Markdown>
          </span>}
      />
      )}
    />
  );
}


const renderRecord = (
  item: TicketProp,
  thisContext: any
): { [x in TicketColumnNameType]: React.ReactElement } => ({
  Id: <LinkRecord ticketId={item.Id} value={item.Id} title={item.Title} />,
  Request: (
    <LinkRecord ticketId={item.Id} value={item.Title} title={item.Title} />
  ),
  Assignee: _getAssignee(item, thisContext),
  Status: (
    <div className={`w-full truncate mr-4`} dir="rtl">
      <bdi>{item.Status}</bdi>
    </div>
  ),
  GroupChat: (
    <>
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {item.GroupChat?.Members && item.GroupChat.Members.length > 0 && (
        <AsyncCircleImage members={item.GroupChat.Members} />
      )}
      {item.GroupChatId ? (
        <a href="#" onClick={() => openGroupChat(item.GroupChat.ConversationId)}>
          {t('ticket.ticket-board.ticket-card.open-chat')}
        </a>
      ) : (
        <span>{t('ticket.ticket-board.ticket-card.no-chat')}</span>
      )}
    </div>
    </>
  ),
  Requester: _getRequester(item, thisContext),
  Lifecycle: (
    <div className={`w-full truncate mr-4`}>
      <bdi>{item.TicketLifecycleName}</bdi>
    </div>
  ),
  Phase: (
    <>
      {item.TicketLifecyclePhaseName.length > 0 && (
        <TicketLifecyclePhaseElement
          status={item["TicketLifecycle"]["Status"]}
          phaseName={item.TicketLifecyclePhaseName}
        />
      )}
    </>
  ),
  Team: <span>{item.TeamName}</span>,
  Group: <span>{item.GroupName}</span>,
  Type: (
    <div className={`w-full truncate mr-4`} dir="rtl">
      <bdi>{item.TicketType}</bdi>
    </div>
  ),
  Category: (
    <div className={`w-full truncate mr-4`} dir="rtl">
      <bdi>{item.Category}</bdi>
    </div>
  ),
  Priority: (
    <div className={`w-full truncate mr-4`} dir="rtl">
      <bdi>{item.Priority}</bdi>
    </div>
  ),
  SLAs:
    item.TicketSlas?.length > 0 ? (
      <SLAPills ticketSlas={item.TicketSlas} />
    ) : (
      <>-</>
    ),
  "Last Modified Date": (
    <span>{ConvertDateToLocale(item.ModifiedDate, DateTime.DATE_SHORT)}</span>
  ),
  "Created Date": (
    <span>{ConvertDateToLocale(item.CreatedDate, DateTime.DATE_SHORT)}</span>
  ),
  "Due Date": (
    <span>
      {ConvertDateToLocale(
        item.DueDate ? item.DueDate : "",
        DateTime.DATE_SHORT
      )}
    </span>
  ),
  'Resolution Date': <span>{ConvertDateToLocale(item.ResolutionDate ? item.ResolutionDate : '', DateTime.DATE_SHORT)}</span>,
  Note: <MarkdownTooltip context={thisContext} details={item.Resolution?.Note ? item.Resolution?.Note : '\u00A0-'} />,
  InternalNote: <MarkdownTooltip context={thisContext} details={item.Resolution?.InternalNote?.length > 0 ? item.Resolution?.InternalNote : '\u00A0-'} /> ,
  DraftKB: <span>{item.Resolution?.DraftKB ? <AcceptIcon style={{paddingLeft: '25px'}}/> : ""}</span>,
  ResolutionCategory: (
    <div className={`w-full truncate mr-4`} dir='rtl'>
      <bdi>{item.ResolutionCategory ? item.ResolutionCategory : '\u00A0-'}</bdi>
    </div>
  ),
  // Formatted through the shared duration renderer so the list agrees with the Time
  // Tracking tab and the Teams card. Zero renders as "0m" rather than a dash: adding this
  // column is a deliberate act, and a real zero is an answer where a dash reads as
  // "unknown". The column is only offered when time tracking is on.
  TotalAgentTime: (
    <span><DurationText minutes={item.TotalTimeSpentMinutes ?? 0} plain /></span>
  ),
});

const renderCPRecordValue =
  (customProperty: ICustomProperty) => (item: TicketProp) => {
    const answerValues = JSON.parse((item.CustomPropertiesAnswerJson?.length ?? 0) > 0 ? item.CustomPropertiesAnswerJson :  "{}");
    const displayValue = getCustomPropertyDisplayData(answerValues, customProperty);
    return (
      <div className={`w-full mr-4`} dir="rtl">
        <bdi>{displayValue}</bdi>
      </div>
    );
  };

const renderRecordValue =
  (colName: TicketColumnNameType, thisContext: any) => (item: TicketProp) => {
    return item.Title === "temp_ticket" ? (
      <SkeletonCell />
    ) : (
      renderRecord(item, thisContext)[colName]
    );
  };

export const _getUser = (email: string, name: string) => (
  <div>
    {_getUserAvatar(email, name)}
    &nbsp;
    {!email ? t("ticket.ticket-board.ticket-card.unassigned") : name}
  </div>
);

export const _getUsers = (users: any[]) =>
  users.length > 0 ? (
    <span className="list-users-cell">
      <GroupUsersIcon maxLength={3} usersList={users} isListing={true} />
    </span>
  ) : (
    <span>-</span>
  );

export const _getUserAvatar = (email: string, name: string) => {
  return email ? (
    <UserAvatar
      {...{
        idOrUpn: email,
        avatarProps: { name: name, size: "smaller", className: "cui-ml-2" },
      }}
    />
  ) : (
    <SpeakerPersonIcon
      size="large"
      style={{ color: "#8f90a6" }}
      variables={{ largeSize: "24px" }}
    />
  );
};
